import { useCallback, useContext } from 'react'
import { AxiosError } from 'axios'

import { ShowWarningAction, WarningContext } from '../../../common'
import {
    AvailableCurrenciesContext,
    mapApiPairDtoToCoinDtos,
    SelectedCoinsContext,
    SetSelectedPairAction,
} from '../../stateManagement'
import { auditPairInputDtos } from '../../utils'
import { ECoinInputType } from '../../constants'

const useOnGetPairErrorHandler = ({
    isClampToRangeEnabled = true,
}: {
    isClampToRangeEnabled?: boolean
} = {}) => {
    const { dispatchAction: dispatchWarningAction } = useContext(WarningContext)
    const {
        state: { from, to },
        dispatchAction: dispatchSelectedCoins,
    } = useContext(SelectedCoinsContext)
    const {
        state: { currencies },
    } = useContext(AvailableCurrenciesContext)

    return useCallback(
        ({
            errorDto,
            dataLayerEvent = 'exchange_widget_error',
            // only if amount has been changed:
            newAmount,
            coinInputType,
        }: {
            newAmount?: number
            coinInputType?: ECoinInputType
            errorDto: AxiosError<any> | any
            dataLayerEvent: string
        }) => {
            const {
                detail,
                pair: { base, quote },
            } = errorDto.response.data

            const { fromCoinInputDto, toCoinInputDto } = mapApiPairDtoToCoinDtos({
                apiPairDto: errorDto.response.data,
                availableCurrencies: currencies,
            })

            if (coinInputType) {
                fromCoinInputDto.amount =
                    coinInputType === ECoinInputType.FROM ? newAmount : from.amount
                toCoinInputDto.amount = coinInputType === ECoinInputType.TO ? newAmount : to.amount

                // 1. clamp amount if out of limit after switch. Coin MIN/MAX - base/quote can be very different:
                const { fixedFromInputDto, fixedToInputDto, errorMessage } = auditPairInputDtos({
                    fromCoinInputDto,
                    toCoinInputDto,
                    isClampToRangeEnabled,
                })

                // 2. update selected pair data:
                dispatchSelectedCoins(
                    new SetSelectedPairAction({
                        availableCurrencies: currencies,
                        from: fixedFromInputDto,
                        to: fixedToInputDto,
                    }),
                )

                if (errorMessage) {
                    dispatchWarningAction(new ShowWarningAction(errorMessage))
                } else {
                    dispatchWarningAction(
                        new ShowWarningAction(
                            'Not able update coins pair data. Please try again later or contact our support team.',
                        ),
                    )
                }
            } else {
                dispatchWarningAction(
                    new ShowWarningAction(
                        'Not able update coins pair data. Please try again later or contact our support team.',
                    ),
                )
            }

            // @ts-ignore
            window.dataLayer.push({
                event: dataLayerEvent,
                pair: `${quote}${base}`,
                details: detail,
            })
        },
        [currencies, dispatchSelectedCoins, dispatchWarningAction],
    )
}

export { useOnGetPairErrorHandler }
